import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import { Link } from 'react-router-dom';
import { useCheckIncludeProfiles } from '@/app/hooks/useAuthorityLevel';
import useAuthorityLevel from '@/app/hooks/useAuthorityLevel';
import usePermission from '@/app/hooks/usePermission';
import { Filter, FilterActions, FilterContent, FilterInput, FilterSelectNative } from '@/components/custom/filter';
import { FilterSelectMulti } from '@/components/custom/filter/filter-select-multi';
import { CourseTabs } from './course-tabs';
import { CourseMonitoring } from './course-monitoring';
import DashboardCourseCard from '../Dashboard/dashboard-course-card';
import CourseRecommended from './CourseRecommended';
import { CourseCertificate } from './course-certificate';
import { CourseModuleAbout } from './course-module-about';
import { useClient } from 'jsonapi-react';
import { Loader } from '@/components/custom/loader';
import { t } from 'i18next';
import { toast } from 'react-hot-toast';
import { EmptyState } from '@/components/custom/empty-state';
import Onboarding from 'src/app/components/Onboarding';
import { useSession } from 'src/app/hooks/useSession';
import { DashboardContent } from '@/layouts/dashboard';
import { Button } from '@/components/ui/button';
import { PageCol, PageContent, PageSidebar } from '@/components/custom/page';
import { CourseCardWrapper } from '@/components/custom/course';

function Course(props) {
  const { uri } = props;

  const [courses, setCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const client = useClient();
  const [loading, setLoading] = useState(false);
  const path = props.path;
  const { session } = useSession();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const profiles = session?.user?.profileNames || (session?.user && session?.user['profile-names']) || [];

  const getCourses = async () => {
    setLoading(true);
    const { data, error } = await client.fetch('/courses?detailed=true');
    if (error) {
      toast.error('Erro ao carregar cursos');
    } else {
      setCourses(data);
    }
    setLoading(false);
  };

  useEffect(() => {
    getCourses();
  }, []);

  const authorityLevel = useAuthorityLevel();

  const filters = useFormik({
    initialValues: {
      visibility: {
        inProgress: false,
        finalized: false,
        published: false,
        notPublished: false
      },
      status: 'blank'
    }
  });

  const options = [
    { name: t('filter.labelInProgress'), id: 1 },
    { name: t('filter.labelCompleted'), id: 2 }
  ];

  const filteredCourses = courses.filter(course => {
    if (searchTerm !== '' && !course.title?.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }

    if (filters.values.status !== 'blank') {
      if (filters.values.status == 1 && course.progress >= 100) {
        return false;
      } else if (filters.values.status == 2 && course.progress < 100) {
        return false;
      }
    }
    return true;
  });

  const breadcrumbs = {
    title: 'Cursos e Aulas',
    nav: [
      {
        route: uri,
        name: 'Cursos',
        isActive: true
      }
    ]
  };

  return (
    <DashboardContent breadcrumbs={breadcrumbs}>
      {useCheckIncludeProfiles({ only: ['student'] }) && <CourseTabs selectedTab="curso" />}

      <Filter>
        <FilterContent>
          <FilterInput
            label="Buscar"
            placeholder={t('textsCommon.research')}
            type="search"
            name="search"
            id="search"
            onChange={e => setSearchTerm(e.target.value)}
          />

          {authorityLevel === 'student' && (
            <FilterSelectNative
              blankLabel={t('filter.blankLabelAll2')}
              label={t('filter.labelSelectStatus')}
              value={filters.values.status}
              onChange={e => filters.setFieldValue('status', e.target.value)}
              options={options}
            />
          )}

          {authorityLevel !== 'student' && (
            <FilterSelectMulti
              data={[
                {
                  label: 'Publicado',
                  value: filters.values.visibility.published,
                  onChange: () => {
                    filters.setFieldValue('visibility', {
                      published: !filters.values.visibility.published,
                      notPublished: false
                    });
                  }
                },
                {
                  label: 'Não publicado',
                  value: filters.values.visibility.finalized,
                  onChange: () => {
                    filters.setFieldValue('visibility', {
                      published: false,
                      notPublished: !filters.values.visibility.notPublished
                    });
                  }
                }
              ]}
            />
          )}
        </FilterContent>

        {usePermission('Criar módulos, disciplinas e aulas', 'Cursos') && (
          <FilterActions>
            <Button
              wide
              asChild
            >
              <Link to="/lms/criar/modulo/curso">{t('button.createModule')}</Link>
            </Button>

            <Button
              wide
              asChild
            >
              <Link to="/lms/gerenciar/modulos">{t('button.manageModules')}</Link>
            </Button>
          </FilterActions>
        )}
      </Filter>

      {loading ? (
        <Loader />
      ) : filteredCourses.length === 0 ? (
        <EmptyState type="data" />
      ) : (
        <PageContent>
          <PageCol>
            <CourseCardWrapper>
              {filteredCourses.map(course => (
                <DashboardCourseCard
                  course={course}
                  key={course.id}
                />
              ))}
            </CourseCardWrapper>
          </PageCol>

          <PageSidebar />
        </PageContent>
      )}

      <Onboarding
        show={showOnboarding}
        setShowOnboarding={setShowOnboarding}
        onClose={() => setShowOnboarding(false)}
        path={path}
        profiles={profiles}
        title={t('onboarding.titleCourse')}
      />
    </DashboardContent>
  );
}

export { Course, CourseMonitoring, CourseRecommended, CourseCertificate, CourseModuleAbout };
